import { useState } from "react";
import axios from "axios";
import { X } from "lucide-react";
import { toast } from "sonner";
import { API, CHURCHES, INCOME_CATEGORIES, errorText, money, todayLocal } from "@/lib/constants";

const NUMERIC = ["dizimo", "oferta", "voto", "cartao", "pix", "dinheiro", "value"];
const blankForm = () => ({ kind: "entrada", date: todayLocal(), church: CHURCHES[0], person_name: "", description: "", category: "Dízimo", tither_name: "", tither_phone: "", paid_from: "conta", dizimo: "", oferta: "", voto: "", cartao: "", pix: "", dinheiro: "", value: "" });

const startForm = (user, entry) => entry
  ? { ...blankForm(), ...entry, paid_from: entry.paid_from || "conta", category: entry.category || (entry.kind === "saida" ? "Outros" : "Dízimo") }
  : { ...blankForm(), church: user.role === "admin" ? CHURCHES[0] : user.church };

const toPayload = (form) => {
  const data = { ...form };
  NUMERIC.forEach(k => { data[k] = Number(data[k] || 0); });
  delete data.id; delete data.created_at;
  return data;
};

const useEntryForm = (user, entry, expenseCategories, onSaved) => {
  const [form, setForm] = useState(() => startForm(user, entry));
  const [saving, setSaving] = useState(false);
  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const setKind = (kind) => setForm(prev => ({ ...prev, kind, category: kind === "entrada" ? "Dízimo" : expenseCategories[0], description: "" }));
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const data = toPayload(form);
      if (entry) await axios.put(`${API}/entries/${entry.id}`, data, { withCredentials: true });
      else await axios.post(`${API}/entries`, data, { withCredentials: true });
      toast.success(entry ? "Lançamento atualizado" : "Lançamento salvo"); onSaved();
    } catch (err) { toast.error(errorText(err, "Verifique os campos do lançamento.")); } finally { setSaving(false); }
  };
  return { form, saving, update, setKind, submit };
};

const MoneyInput = ({ label, field, testid, form, update }) => <label>{label}<input data-testid={testid} type="number" step="0.01" min="0" value={form[field]} onChange={e => update(field, e.target.value)} placeholder="R$ 0,00" /></label>;

const IncomeFields = ({ form, update }) => {
  const tithe = form.category === "Dízimo";
  const total = Number(form.pix || 0) + Number(form.cartao || 0) + Number(form.dinheiro || 0);
  return <>
    <label className="wide">Tipo de entrada<select data-testid="entry-income-category-select" value={form.category} onChange={e => update("category", e.target.value)}>{INCOME_CATEGORIES.map(c => <option key={c}>{c}</option>)}</select></label>
    {tithe && <label className="wide">Nome do dizimista<input data-testid="entry-tither-name-input" required value={form.tither_name} onChange={e => update("tither_name", e.target.value)} placeholder="Nome de quem entregou o dízimo" /></label>}
    {tithe && <label className="wide">WhatsApp do dizimista (opcional)<input data-testid="entry-tither-phone-input" value={form.tither_phone} onChange={e => update("tither_phone", e.target.value)} placeholder="(11) 90000-0000" /></label>}
    <MoneyInput label="Pix" field="pix" testid="entry-pix-input" form={form} update={update} />
    <MoneyInput label="Cartão" field="cartao" testid="entry-card-input" form={form} update={update} />
    <MoneyInput label="Dinheiro" field="dinheiro" testid="entry-cash-input" form={form} update={update} />
    <div className="entry-total" data-testid="entry-income-total"><span>Total {form.category}</span><strong>{money(total)}</strong></div>
  </>;
};

const ExpenseFields = ({ form, update, expenseCategories }) => <>
  <label className="wide">Tipo de saída<select data-testid="entry-expense-category-select" value={form.category} onChange={e => update("category", e.target.value)}>{expenseCategories.map(c => <option key={c}>{c}</option>)}</select></label>
  {form.category === "Outros" && <label className="wide">Descreva a saída<textarea data-testid="entry-description-input" required value={form.description} onChange={e => update("description", e.target.value)} placeholder="Escreva o motivo da saída" /></label>}
  <div className="wide paid-from"><span>Pago por</span><div className="segmented">
    <button type="button" data-testid="entry-paid-conta-button" className={form.paid_from !== "fisico" ? "active" : ""} onClick={() => update("paid_from", "conta")}>Conta (banco)</button>
    <button type="button" data-testid="entry-paid-fisico-button" className={form.paid_from === "fisico" ? "active" : ""} onClick={() => update("paid_from", "fisico")}>Físico (dinheiro)</button>
  </div></div>
  <label className="wide">Valor da saída<input data-testid="entry-expense-value-input" required type="number" step="0.01" min="0.01" value={form.value} onChange={e => update("value", e.target.value)} placeholder="R$ 0,00" /></label>
</>;

const modalTitle = (entry, kind) => {
  if (entry) return "Editar lançamento";
  return kind === "entrada" ? "Registrar entrada" : "Registrar saída";
};
const saveLabel = (entry, saving) => {
  if (saving) return "Salvando...";
  return entry ? "Salvar alterações" : "Salvar lançamento";
};

export const EntryModal = ({ user, entry, expenseCategories, onClose, onSaved }) => {
  const { form, saving, update, setKind, submit } = useEntryForm(user, entry, expenseCategories, onSaved);
  const title = modalTitle(entry, form.kind);
  return <div className="modal-backdrop"><div className="modal">
    <button data-testid="entry-modal-close-button" className="icon-btn close" onClick={onClose}><X /></button>
    <p className="eyebrow">{entry ? "EDITAR LANÇAMENTO" : "NOVO LANÇAMENTO"}</p><h2>{title}</h2>
    <form onSubmit={submit}>
      <div className="segmented">
        <button data-testid="entry-kind-income-button" type="button" className={form.kind === "entrada" ? "active" : ""} onClick={() => setKind("entrada")}>Entrada</button>
        <button data-testid="entry-kind-expense-button" type="button" className={form.kind === "saida" ? "active" : ""} onClick={() => setKind("saida")}>Saída</button>
      </div>
      <div className="form-grid">
        <label>Data<input data-testid="entry-date-input" type="date" value={form.date} onChange={e => update("date", e.target.value)} /></label>
        <label>Igreja{user.role === "admin" ? <select data-testid="entry-church-select" value={form.church} onChange={e => update("church", e.target.value)}>{CHURCHES.map(c => <option key={c}>{c}</option>)}</select> : <input data-testid="entry-church-readonly" value={user.church} readOnly />}</label>
        <label className="wide">Nome<input data-testid="entry-person-name-input" required value={form.person_name} onChange={e => update("person_name", e.target.value)} placeholder="Nome do responsável" /></label>
        {form.kind === "entrada" ? <IncomeFields form={form} update={update} /> : <ExpenseFields form={form} update={update} expenseCategories={expenseCategories} />}
      </div>
      <button data-testid="entry-save-button" className="primary-btn" disabled={saving}>{saveLabel(entry, saving)}</button>
    </form>
  </div></div>;
};
